import React, { useState, useEffect } from "react";
import {
    Box,
    Button,
    FormControl,
    FormLabel,
    Input,
    Select,
    Stack,
    Heading,
    Container,
    useColorModeValue,
    useToast
} from '@chakra-ui/react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import API_BASE from "../apiConfig";

function ProfileSetupPage() {
    const navigate = useNavigate();
    const toast = useToast();
    const [formData, setFormData] = useState({
        first_name: '',
        last_name: '',
        age: '',
        gender: '',
        height: '',
        weight: ''
    });

    const bgColor = useColorModeValue('white', 'gray.800');

    const [loading, setLoading] = useState(true);

    useEffect(() => {
        //Fetch user info to pre-fill fields if any
        const fetchProfile = async () => {
            try {
                const token = localStorage.getItem('token');
                const res = await axios.get(`${API_BASE}/auth/me`, {
                    headers: { Authorization: `Bearer ${token}`}
                });
                const { first_name, last_name, age, gender, height, weight } = res.data;
                setFormData({
                    first_name: first_name || '',
                    last_name: last_name || '',
                    age: age || '',
                    gender: gender || '',
                    height: height || '',
                    weight: weight || '',
                });
            } catch (err) {
                console.error('Failed to fetch user info', err);
            } finally {
                setLoading(false);
            }
        };

        fetchProfile();
    }, []);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData((prev) => ({ ...prev, [name]: value}));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (formData.age <= 0) {
            return toast({
                title: 'Age must be greater than 0',
                status: 'error',
                duration: 3000,
                isClosable: true,
            });
        }
        
        if (formData.age <= 0 || !['male', 'female', 'other' ].includes(formData.gender)) {
            toast({
                title: 'Invalid input',
                status: 'error',
                duration: 3000, 
                isClosable: true,
            });
            return;
        }

        try {
            const token = localStorage.getItem('token');
            await axios.put(`${API_BASE}/auth/me`, formData, {
                headers: {
                    Authorization: `Bearer ${token}`,
                },
            });

            toast({
                title: 'Profile updated successfully!',
                status: 'success',
                duration: 2000,
                isClosable: true, 
            });

            navigate('/');
        } catch (err) {
            toast({
                title: 'Failed to updated profile',
                status: 'error',
                duration: 3000,
                isClosable: true, 
            });
        }
    };

    if (loading) {
        return (
            <Container maxW="md" py={20}>
                <Box textAlign="center">
                    <Heading size="md" color="green.600">Loading profile...</Heading>
                </Box>
            </Container>
        );
    }

    return (
        <Container maxW="md" py={10}>
            <Box 
                borderWidth="1px"
                borderRadius="lg"
                p={8}
                boxShadow="md"
                bg={bgColor} 
            >
                <Heading size="lg" mb={6} textAlign="center" color="green.600">Complete your profile</Heading>
                <form onSubmit={handleSubmit}>
                    <Stack spacing={5}>
                        <FormControl isRequired>
                            <FormLabel>First Name</FormLabel>
                            <Input name="first_name" value={formData.first_name} onChange={handleChange} />
                        </FormControl>
                        <FormControl isRequired>
                            <FormLabel>Last Name</FormLabel>
                            <Input name="last_name" value={formData.last_name} onChange={handleChange} />
                        </FormControl>
                        <FormControl isRequired>
                            <FormLabel>Age</FormLabel>
                            <Input type="number" name="age" value={formData.age} onChange={handleChange} min={1} />
                        </FormControl>
                        <FormControl isRequired>
                            <FormLabel>Gender</FormLabel>
                            <Select name="gender" value={formData.gender} onChange={handleChange}>
                                <option value="">Select Gender</option>
                                <option value="male">Male</option>
                                <option value="female">Female</option>
                                <option value="other">Other</option>
                            </Select>
                        </FormControl>
                        <FormControl isRequired>
                            <FormLabel>Height (cm)</FormLabel>
                            <Input type="number" name="height" value={formData.height} onChange={handleChange} min={1} />
                        </FormControl>
                        <FormControl isRequired>
                            <FormLabel>Weight (kg)</FormLabel>
                            <Input type="number" name="weight" value={formData.weight} onChange={handleChange} min={1} />
                        </FormControl>
                        <Button colorScheme="green" type="submit" width="full">Save Profile</Button>
                    </Stack>
                </form>
            </Box>
        </Container>
    );
}

export default ProfileSetupPage;